import React from 'react';

function hostInfo(host) {
  const raw = String(host || '').trim().toLowerCase();
  if (raw === 'gpu' || raw === 'spc' || raw === 'server107' || raw === 'server_107' || raw === 'server 107') {
    return { letter: 'S', title: 'Spc', color: '#1e3a8a', background: '#dbeafe', border: '#93c5fd' };
  }
  if (raw === 'mac') {
    return { letter: 'M', title: 'Mac', color: '#9a3412', background: '#ffedd5', border: '#fdba74' };
  }
  return null;
}

// Short mark printed next to the host letter. Gemini is the version alone
// (3.8, 3.1). A local model adds one letter: G gemma, Q qwen.
function modelTag(model) {
  const raw = String(model || '').trim().toLowerCase();
  if (!raw) return null;
  if (raw === 'ggml' || raw.includes('gemma')) return { code: 'G', name: 'gemma' };
  if (raw.includes('qwen')) return { code: 'Q', name: raw };
  const gemini = raw.match(/^gemini-(\d+(?:\.\d+)?)/);
  if (gemini) return { code: gemini[1], name: raw, gemini: true };
  if (raw.startsWith('gemini')) return { code: 'G', name: raw, gemini: true };
  return { code: '', name: raw };
}

const GEMINI_STYLE = { color: '#5b21b6', background: '#ede9fe', border: '#c4b5fd' };

/**
 * Letter for a graded paper.
 * Host only (check-table badge): S = Spc, M = Mac.
 * Host and model (print preview): SG = Spc gemma, MG = Mac gemma,
 * 3.8 = gemini-3.8-flash, 3.1 = gemini-3.1-pro-preview.
 * A local host passed with an empty model is gemma: that column was not
 * written on the phased grading path, and those machines grade with gemma.
 */
export function gradingMachine(host, model) {
  const machine = hostInfo(host);
  const includeModel = arguments.length > 1;
  const tag = includeModel ? modelTag(model) : null;
  if (tag && tag.gemini) {
    return { letter: tag.code, title: tag.name, ...GEMINI_STYLE };
  }
  if (tag && tag.code && machine) {
    return { ...machine, letter: machine.letter + tag.code, title: `${machine.title} · ${tag.name}` };
  }
  if (tag && tag.code) {
    return { letter: tag.code, title: tag.name, color: '#166534', background: '#dcfce7', border: '#86efac' };
  }
  if (includeModel && machine && !tag) {
    return { ...machine, letter: machine.letter + 'G', title: `${machine.title} · gemma` };
  }
  if (machine) return { ...machine, title: tag && tag.name ? `${machine.title} · ${tag.name}` : machine.title };
  return null;
}

export const GRADING_MACHINE_LEGEND = 'S = Spc, M = Mac';

export function hostLabel(host) {
  const machine = hostInfo(host);
  if (machine) return machine.title;
  return String(host || '').trim();
}

// Gemini runs on Gemini. The header drops the gemini- prefix, so
// gemini-3.8-flash shows as "3.8-flash · Gemini". Local models keep Spc or Mac.
// The saved model id is unchanged.
export function stageModelLabel(model, host) {
  const name = String(model || '').trim();
  const tag = modelTag(name);
  if (tag && tag.gemini) {
    const short = name.replace(/^gemini-/i, '');
    return [short, 'Gemini'].filter(Boolean).join(' · ');
  }
  return [name, hostLabel(host)].filter(Boolean).join(' · ');
}

export function GradingMachineBadge({ host }) {
  const machine = gradingMachine(host);
  const shared = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 18,
    height: 18,
    padding: '0 4px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1,
    flexShrink: 0
  };
  if (!machine) {
    return (
      <span title="Grading machine was not recorded" style={{ ...shared, color: '#9ca3af' }}>—</span>
    );
  }
  return (
    <span
      title={machine.title}
      style={{
        ...shared,
        color: machine.color,
        background: machine.background,
        border: `1px solid ${machine.border}`
      }}
    >
      {machine.letter}
    </span>
  );
}
